import React, { useState, useRef, useEffect, useCallback } from "react";
import { App, Modal } from "antd";
import {
  UploadOutlined, FilePdfOutlined, EditOutlined,
  DownloadOutlined, DeleteOutlined, CheckCircleFilled,
  UndoOutlined, ClearOutlined, CheckOutlined,
  PictureOutlined, FontSizeOutlined, CalendarOutlined,
  UserOutlined, SafetyCertificateOutlined,
} from "@ant-design/icons";
import axios from "axios";
import HeaderLayout from "../../components/Header";
import FooterLayout from "../../components/Footer";
import PageHeader from "../../components/PageHeader";
import { downloadAuthenticatedFile } from "../../utils/downloadFile";
import "../PdfTools.css";
import "./ESignPdf.css";

const countPdfPages = async (pdfFile) => {
  const buffer = await pdfFile.arrayBuffer();
  const pdfText = new TextDecoder("latin1").decode(buffer);
  const pageMatches = pdfText.match(/\/Type\s*\/Page\b(?!s)/g);

  if (pageMatches?.length) {
    return pageMatches.length;
  }

  const countMatches = [...pdfText.matchAll(/\/Count\s+(\d+)/g)]
    .map((match) => Number(match[1]))
    .filter((count) => Number.isFinite(count) && count > 0);

  return countMatches.length ? Math.max(...countMatches) : 0;
};

// ── Signature Canvas ──────────────────────────────────────────────
const SignatureCanvas = ({ onDone, onCancel }) => {
  const canvasRef   = useRef(null);
  const [drawing, setDrawing] = useState(false);
  const [strokes,  setStrokes]  = useState([]); // array of arrays of {x,y}
  const [redoStack, setRedoStack] = useState([]);
  const [color,    setColor]    = useState("#000000");

  const getPos = (e, canvas) => {
    const rect = canvas.getBoundingClientRect();
    const src  = e.touches ? e.touches[0] : e;
    return { x: src.clientX - rect.left, y: src.clientY - rect.top };
  };

  const redraw = useCallback((stks, col) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = col || color;
    ctx.lineWidth = 3;
    ctx.lineCap = "round";
    stks.forEach((stroke) => {
      if (stroke.length < 2) return;
      ctx.beginPath();
      ctx.moveTo(stroke[0].x, stroke[0].y);
      stroke.slice(1).forEach((p) => ctx.lineTo(p.x, p.y));
      ctx.stroke();
    });
  }, [color]);

  const startStroke = (e) => {
    e.preventDefault();
    const pos = getPos(e, canvasRef.current);
    setStrokes((prev) => [...prev, [pos]]);
    setRedoStack([]);
    setDrawing(true);
  };

  const addPoint = (e) => {
    e.preventDefault();
    if (!drawing) return;
    const pos = getPos(e, canvasRef.current);
    setStrokes((prev) => {
      const next = [...prev];
      next[next.length - 1] = [...next[next.length - 1], pos];
      return next;
    });
  };

  const endStroke = () => setDrawing(false);

  const undo = () => {
    setStrokes((prev) => {
      if (prev.length === 0) return prev;
      const last = prev[prev.length - 1];
      setRedoStack((r) => [...r, last]);
      return prev.slice(0, -1);
    });
  };

  const redo = () => {
    setRedoStack((prev) => {
      if (prev.length === 0) return prev;
      const last = prev[prev.length - 1];
      setStrokes((s) => [...s, last]);
      return prev.slice(0, -1);
    });
  };

  const clear = () => { setStrokes([]); setRedoStack([]); };

  useEffect(() => { redraw(strokes, color); }, [strokes, color, redraw]);

  const handleDone = () => {
    if (strokes.length === 0) { onCancel(); return; }
    const canvas = canvasRef.current;
    const dataUrl = canvas.toDataURL("image/png");
    onDone(dataUrl);
  };

  const COLORS = ["#000000", "#e5322d", "#1a3f9e"];

  return (
    <div className="sig-wrap">
      <canvas
        ref={canvasRef}
        width={580}
        height={260}
        className="sig-canvas"
        onMouseDown={startStroke}
        onMouseMove={addPoint}
        onMouseUp={endStroke}
        onMouseLeave={endStroke}
        onTouchStart={startStroke}
        onTouchMove={addPoint}
        onTouchEnd={endStroke}
      />
      <div className="sig-toolbar">
        <div className="sig-colors">
          {COLORS.map((c) => (
            <button key={c} className={`sig-color-btn ${color === c ? "active" : ""}`}
              style={{ background: c }} onClick={() => setColor(c)} />
          ))}
        </div>
        <div className="sig-actions">
          <button className="sig-tool-btn" onClick={undo} title="Undo"><UndoOutlined /></button>
          <button className="sig-tool-btn" onClick={clear} title="Clear"><ClearOutlined /></button>
          <button className="sig-done-btn" onClick={handleDone}><CheckOutlined /> Done</button>
        </div>
      </div>
    </div>
  );
};

// ── Main Component ────────────────────────────────────────────────
const ESignPdf = () => {
  const { message } = App.useApp();
  const [file,         setFile]         = useState(null);
  const [readingPages, setReadingPages] = useState(false);
  const [pageReadFailed, setPageReadFailed] = useState(false);
  const [pageCount,    setPageCount]    = useState(0);
  const [selectedPage, setSelectedPage] = useState(null);
  const [sigDataUrl,   setSigDataUrl]   = useState(null);
  const [sigPos,       setSigPos]       = useState({ x: 20, y: 20 });
  const [sigScale,     setSigScale]     = useState(1.0);
  const [draggingSig,  setDraggingSig]  = useState(false);
  const [dragStart,    setDragStart]    = useState({ x: 0, y: 0 });
  const [showSigModal, setShowSigModal] = useState(false);
  const [uploading,    setUploading]    = useState(false);
  const [downloading,  setDownloading]  = useState(false);
  const [dragOver,     setDragOver]     = useState(false);
  const [downloadUrl,  setDownloadUrl]  = useState("");
  const [signedFileName, setSignedFileName] = useState("");
  const fileRef    = useRef(null);
  const sigImgRef  = useRef(null);

  const isModule = window.location.pathname.includes("/dashboard/");

  const pickFile = async (incoming) => {
    const f = Array.from(incoming).find((f) =>
      f.type === "application/pdf" || f.name.endsWith(".pdf")
    );
    if (!f) { message.error("Please select a PDF file."); return; }
    setFile(f); setDownloadUrl(""); setSigDataUrl(null);
    setSignedFileName(""); setDownloading(false);
    setSelectedPage(null); setPageCount(0);
    setPageReadFailed(false);
    setReadingPages(true);

    try {
      const detectedPageCount = await countPdfPages(f);
      if (detectedPageCount > 0) {
        setPageCount(detectedPageCount);
      } else {
        setPageReadFailed(true);
        message.warning("Unable to read page count automatically. Please enter it manually.");
      }
    } catch {
      setPageReadFailed(true);
      message.warning("Unable to read page count automatically. Please enter it manually.");
    } finally {
      setReadingPages(false);
    }
  };

  const reset = () => {
    setFile(null); setDownloadUrl(""); setSigDataUrl(null);
    setSignedFileName(""); setDownloading(false);
    setSelectedPage(null); setPageCount(0);
    setPageReadFailed(false);
    setReadingPages(false);
  };

  const handleSignatureDone = (dataUrl) => {
    setSigDataUrl(dataUrl);
    setSigPos({ x: 20, y: 20 });
    setSigScale(1.0);
    setShowSigModal(false);
  };

  const pickSigFromGallery = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      setSigDataUrl(ev.target.result);
      setSigPos({ x: 20, y: 20 }); setSigScale(1.0);
      setShowSigModal(false);
    };
    reader.readAsDataURL(f);
  };

  // Drag signature on the preview
  const onSigMouseDown = (e) => {
    e.preventDefault(); e.stopPropagation();
    setDraggingSig(true);
    setDragStart({ x: e.clientX - sigPos.x, y: e.clientY - sigPos.y });
  };
  useEffect(() => {
    const move = (e) => {
      if (!draggingSig) return;
      setSigPos({ x: e.clientX - dragStart.x, y: e.clientY - dragStart.y });
    };
    const up = () => setDraggingSig(false);
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
    return () => { window.removeEventListener("mousemove", move); window.removeEventListener("mouseup", up); };
  }, [draggingSig, dragStart]);

  // Submit — upload signature image + PDF page info to image-to-pdf flow
  const handleSave = async () => {
    if (!file || !sigDataUrl || selectedPage === null) return;
    setUploading(true);
    setDownloadUrl("");

    try {
      // Convert sigDataUrl to Blob
      const sigRes  = await fetch(sigDataUrl);
      const sigBlob = await sigRes.blob();
      const sigFile = new File([sigBlob], "signature.png", { type: "image/png" });

      const formData = new FormData();
      formData.append("file", file);
      formData.append("signatureFile", sigFile);
      formData.append("pageNumber", (selectedPage + 1).toString());
      formData.append("positionX", Math.round(sigPos.x).toString());
      formData.append("positionY", Math.round(sigPos.y).toString());
      formData.append("scale", sigScale.toFixed(2));
      formData.append("Source", isModule ? "Private-Web" : "Public-Web");

      const res = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/api/Pdf/esign-pdf`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token") ?? ""}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (!res.data.error && res.data.data?.downloadUrl) {
        setDownloadUrl(res.data.data.downloadUrl);
        setSignedFileName(res.data.data.fileName || "signed.pdf");
        message.success("PDF signed successfully!");
      } else {
        message.error(res.data.message || "E-sign failed.");
      }
    } catch (err) {
      message.error(err?.response?.data?.message || "Something went wrong.");
    } finally {
      setUploading(false);
    }
  };

  const downloadSignedPdf = async () => {
    if (!downloadUrl || downloading) return;
    setDownloading(true);

    try {
      await downloadAuthenticatedFile(downloadUrl, {
        token: localStorage.getItem("token") ?? "",
        fileName: signedFileName || "signed.pdf",
      });
    } catch {
      message.error("Unable to download signed PDF. Please try again.");
    } finally {
      setDownloading(false);
    }
  };

  const pages = pageCount > 0
    ? Array.from({ length: pageCount }, (_, i) => i)
    : [];

  return (
    <>
      {isModule ? <PageHeader /> : <HeaderLayout />}

      <div className={`pt-root ${isModule ? "pt-module" : ""}`}>
        <div className="pt-inner pt-wide">

          <div className="pt-header">
            <div className="pt-header-icon" style={{ background: "#fff7ed" }}>
              <EditOutlined style={{ color: "#c84b1e" }} />
            </div>
            <div>
              <h1 className="pt-title">E-sign PDF</h1>
              <p className="pt-subtitle">
                Upload your PDF, draw or upload your signature, position it on a page, then download the signed PDF.
              </p>
            </div>
          </div>

          {/* Step 1 — pick file */}
          {!file && !downloadUrl && (
            <div
              className={`pt-dropzone ${dragOver ? "drag-over" : ""}`}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => { e.preventDefault(); setDragOver(false); pickFile(e.dataTransfer.files); }}
              onClick={() => fileRef.current?.click()}
            >
              <input ref={fileRef} type="file" accept=".pdf,application/pdf" style={{ display: "none" }}
                onChange={(e) => { pickFile(e.target.files); e.target.value = ""; }} />
              <FilePdfOutlined className="pt-dropzone-icon" />
              <div className="pt-dropzone-text">Click to select or drag your PDF here</div>
              <div className="pt-dropzone-hint">Only PDF files supported</div>
              <button className="pt-choose-btn" onClick={(e) => { e.stopPropagation(); fileRef.current?.click(); }}>
                <UploadOutlined /> Choose File
              </button>
            </div>
          )}

          {file && !downloadUrl && (
            <div className="es-flow">
              {/* File row */}
              <div className="pt-file-row">
                <FilePdfOutlined className="pt-file-icon" />
                <span className="pt-file-name">{file.name}</span>
                <span className="pt-file-size">{(file.size / 1024).toFixed(0)} KB</span>
                <button className="pt-file-remove" onClick={reset}><DeleteOutlined /></button>
              </div>

              {/* Step 2 — page count */}
              {(readingPages || pageReadFailed || pageCount > 0) && selectedPage === null && (
                <div className="es-step-card">
                  <div className="es-step-num">1</div>
                  <div className="es-step-body">
                    <div className="es-step-title">
                      {readingPages
                        ? "Reading PDF page count..."
                        : pageReadFailed
                          ? "How many pages does this PDF have?"
                          : "PDF pages detected"}
                    </div>
                    {readingPages ? (
                      <div className="es-page-status">
                        <span className="pt-spinner" /> Checking
                      </div>
                    ) : pageReadFailed ? (
                      <div className="es-step-row">
                        <input type="number" min="1" max="500" className="es-num-input" placeholder="e.g. 5"
                          id="es-page-count" />
                        <button className="pt-choose-btn" style={{ marginTop: 0 }}
                          onClick={() => {
                            const v = parseInt(document.getElementById("es-page-count").value);
                            if (v > 0) {
                              setPageCount(v);
                              setPageReadFailed(false);
                            }
                          }}>
                          Confirm
                        </button>
                      </div>
                    ) : (
                      <div className="es-page-detected">{pageCount} page{pageCount !== 1 ? "s" : ""}</div>
                    )}
                  </div>
                </div>
              )}

              {/* Step 3 — select page */}
              {pageCount > 0 && selectedPage === null && (
                <div className="es-step-card">
                  <div className="es-step-num">2</div>
                  <div className="es-step-body">
                    <div className="es-step-title">Which page do you want to sign?</div>
                    <div className="es-page-tiles">
                      {pages.map((i) => (
                        <button key={i} className="es-page-tile" onClick={() => setSelectedPage(i)}>
                          <FilePdfOutlined style={{ fontSize: 20, color: "#e5322d" }} />
                          <span>Page {i + 1}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Step 4 — add signature */}
              {selectedPage !== null && !sigDataUrl && (
                <div className="es-step-card">
                  <div className="es-step-num">3</div>
                  <div className="es-step-body">
                    <div className="es-step-title">Add your signature to Page {selectedPage + 1}</div>
                    <div className="es-sig-options">
                      <button className="es-sig-option" onClick={() => setShowSigModal(true)}>
                        <EditOutlined style={{ fontSize: 22, color: "#e5322d" }} />
                        <span>Draw Signature</span>
                      </button>
                      <label className="es-sig-option">
                        <PictureOutlined style={{ fontSize: 22, color: "#6366f1" }} />
                        <span>Upload Image</span>
                        <input type="file" accept="image/*" style={{ display: "none" }} onChange={pickSigFromGallery} />
                      </label>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 5 — position signature */}
              {selectedPage !== null && sigDataUrl && (
                <div className="es-step-card">
                  <div className="es-step-num">4</div>
                  <div className="es-step-body">
                    <div className="es-step-title">
                      Drag signature to position · Use +/− to resize
                    </div>
                    <div className="es-preview-area">
                      <div className="es-page-preview">
                        <div className="es-page-label-preview">Page {selectedPage + 1}</div>
                        <div
                          className="es-sig-overlay"
                          style={{ left: sigPos.x, top: sigPos.y, cursor: draggingSig ? "grabbing" : "grab" }}
                          onMouseDown={onSigMouseDown}
                        >
                          <img
                            ref={sigImgRef}
                            src={sigDataUrl}
                            alt="Signature"
                            style={{ width: 150 * sigScale, display: "block" }}
                            draggable={false}
                          />
                        </div>
                      </div>
                      <div className="es-scale-bar">
                        <button className="es-scale-btn" onClick={() => setSigScale((s) => Math.max(0.3, s - 0.1))}>−</button>
                        <span className="es-scale-val">{Math.round(sigScale * 100)}%</span>
                        <button className="es-scale-btn" onClick={() => setSigScale((s) => Math.min(5, s + 0.1))}>+</button>
                        <button className="es-sig-change" onClick={() => setSigDataUrl(null)}>Change signature</button>
                      </div>
                    </div>
                    <button className="pt-action-btn" onClick={handleSave} disabled={uploading}>
                      {uploading ? <span className="pt-spinner" /> : <CheckOutlined />}
                      {uploading ? "Saving signed PDF…" : "Save Signed PDF"}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Success */}
          {downloadUrl && (
            <div className="pt-success">
              <CheckCircleFilled className="pt-success-icon" />
              <div className="pt-success-title">PDF Signed!</div>
              <p className="pt-success-sub">Your signature has been applied to the PDF.</p>
              <div className="pt-success-actions">
                <button
                  type="button"
                  className="pt-download-btn"
                  onClick={downloadSignedPdf}
                  disabled={downloading}
                >
                  {downloading ? <span className="pt-spinner" /> : <DownloadOutlined />}
                  {downloading ? "Downloading..." : "Download Signed PDF"}
                </button>
                <button className="pt-restart-btn" onClick={reset}>Sign another</button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Signature draw modal */}
      <Modal
        open={showSigModal}
        onCancel={() => setShowSigModal(false)}
        footer={null}
        title={<span style={{ fontFamily: "Poppins", fontWeight: 700 }}>✍️ Draw Your Signature</span>}
        width={640}
        centered
        destroyOnClose
      >
        <SignatureCanvas
          onDone={handleSignatureDone}
          onCancel={() => setShowSigModal(false)}
        />
      </Modal>

      {!isModule && <FooterLayout />}
    </>
  );
};

export default ESignPdf;
